/**
 * MfaEnrollmentGate (ACC-05)
 * Shown instead of the app when /users/me/permissions reports
 * mfa.required && !mfa.enrolled. The user must enroll a TOTP app to continue.
 * Browser-only gate (decision D1, 2026-10-07); inactive while the backend
 * MFA_ENFORCEMENT flag is off, because mfa.required is then always false.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/authcontext';
import { startTotpEnrollment, completeTotpEnrollment } from '../../../services/auth/cognito.service';
import MfaSetup from './MfaSetup';

interface MfaEnrollmentGateProps {
  onLogout: () => void | Promise<void>;
}

const MfaEnrollmentGate: React.FC<MfaEnrollmentGateProps> = ({ onLogout }) => {
  const { user, loadPermissions } = useAuth();
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const email = user?.email || '';

  useEffect(() => {
    let cancelled = false;
    startTotpEnrollment(email)
      .then((url) => { if (!cancelled) setQrCodeUrl(url); })
      .catch(() => { if (!cancelled) setError('Failed to start MFA setup. Please sign in again.'); });
    return () => { cancelled = true; };
  }, [email]);

  const handleVerify = async (code: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await completeTotpEnrollment(code);
      // Reload permissions: mfa.enrolled becomes true and the gate lifts
      await loadPermissions();
    } catch {
      setError('Invalid verification code. Please try again with a new code from your authenticator app.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-white px-4 py-8">
      <p className="mb-4 max-w-sm text-sm text-slate-600">
        Your organization requires two-factor authentication for your role. Set it up to continue.
      </p>
      <MfaSetup
        qrCodeUrl={qrCodeUrl}
        handleVerifyMfaSetup={handleVerify}
        error={error}
        isLoading={isLoading}
        allowSkip={false}
      />
      <button
        type="button"
        onClick={() => { void onLogout(); }}
        className="mt-4 text-sm text-blue-600 hover:underline"
      >
        Sign out
      </button>
    </main>
  );
};

export default MfaEnrollmentGate;
